import { Fragment, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { SteuerRueckfrageHost } from "../SteuerRueckfrage";
import { ERechnungHost } from "../ERechnungAnsicht";
import { Link, Outlet, useLocation } from "react-router";
import { Icon, type IconName } from "../Icon";
import { CommandPalette, showCommandPalette, type PaletteItem } from "./CommandPalette";
import { autoThemeForAccount, toggleThemeForAccount, useThemeFromAccount } from "../Darstellung";
import { currentThemePreference } from "../../lib/theme";
import { Toasts } from "../Toast";
import { accentCss } from "../../lib/color";
import { portalTitle, useDocumentTitle } from "./branding";
import { CreatorOfficeLogo, RahmenKontext } from "./Logo";
import { ScrollToTop } from "./ScrollToTop";
import { useDocumentLang } from "./WebLayout";
import { t } from "../../lib/sprache";

/**
 * Rahmen der Portalseiten: Kopfzeile über die volle Breite (CreatorOffice-Logo, Glocke, Konto-Chip mit Menü), dunkelgrüne
 * Seitenleiste, runder Chat-Knopf unten rechts, mobile Leiste unten, Befehlspalette. Seit Revision 5 im Look des
 * Original-Portals: Suche und Hell/Dunkel liegen im Menü des Konto-Chips (Strg+K öffnet die Suche weiterhin).
 * Verbindliche Regel (Marke CreatorOffice, 29.09.2026): oben links steht in Agentur- und Creator-Portal IMMER das
 * CreatorOffice-Logo (am Handy nur das Zeichen). Logo und eigener Portalname der Agentur ersetzen es nicht; das Agenturlogo
 * steht nur auf Dokumenten und Außendarstellung der Agentur, im Creator-Portal zusätzlich als Inhalt („Deine Agentur: …“).
 */

export interface NavItem {
  label: string;
  to: string;
  icon: IconName;
  /** Weitere Pfade, bei denen der Eintrag als aktiver Bereich gilt. */
  also?: string[];
  /** Zahl am Eintrag (z. B. ungelesene Nachrichten) */
  badge?: number;
  /** Art der Zahl für Screenreader: ungelesen (Standard) oder neu (Kalender, PARITAET-19) */
  badgeKind?: "unread" | "new";
  end?: boolean;
  /** Überschrift einer Gruppe im Seitenmenü, steht vor diesem Eintrag (Verständlichkeits-Lauf V-10: 16 Punkte ohne Gliederung) */
  group?: string;
}

export interface PortalLayoutProps {
  brandHref: string;
  orgName: string;
  nav: NavItem[];
  bottom: NavItem[];
  palette: PaletteItem[];
  /** Suche in den Daten für die Lupe (K-121); ohne Angabe findet sie nur Seiten */
  suche?: (q: string) => Promise<PaletteItem[]>;
  /**
   * Kontomenü (K-370, Aufbau wie bei Notion, Slack und HubSpot): Kopf mit Name, Agentur und Tarif als Badge, dann Mein Profil
   * (`href`), Einstellungen, Abo & Tarif, Hilfe & Support. Fehlende Ziele lassen den Eintrag weg.
   */
  account: {
    name: string; initials: string; href: string;
    /** Zweite Zeile im Kopf (Agentur bzw. Konto) */
    org?: string;
    /** Tarif als Badge im Kopf */
    plan?: string | null;
    settingsHref?: string;
    aboHref?: string;
    helpHref?: string;
    /** Beschriftung von „Mein Profil“ (Creator-Konto: „Mein Zugang“) */
    profileLabel?: string;
    /** IA-1: Persönliches im Profilmenü (Anmeldung & Sicherheit, Benachrichtigungen, Sprache, Design) */
    securityHref?: string;
    notificationsHref?: string;
    languageHref?: string;
    designHref?: string;
  };
  /** IA-1: Zahnrad neben Glocke und Profil öffnet „Alle Einstellungen“ */
  gearHref?: string;
  /**
   * IA-1: Vollbild „Alle Einstellungen“: statt Kopfzeile, Seitenleiste und unterer Leiste nur diese Kopfzeile und der Inhalt
   * (Arbeitsnavigation ganz ausgeblendet).
   */
  vollbild?: ReactNode;
  /** Hervorgehobener Eintrag „Upgrade auf …“ im Kontomenü (nur Admins mit größerem buchbarem Tarif) */
  upgrade?: { label: string; to: string } | null;
  /** Inhalt unten in der Seitenleiste (Tarifkarte, K-371) */
  sideExtra?: ReactNode;
  /** Abmelden direkt aus dem Kontomenü (Praxistest K-078); ohne Angabe fehlt der Eintrag */
  onSignOut?: () => void;
  bell?: { href: string; unread: number };
  /** Wechsel im Kopf: Agenturen im Creator-Portal; S2 (0540) auch „Mein Konto“ und „Bei <Agentur>“ (label: Beschriftung für Vorleser) */
  agencyChoice?: { value: string; options: { value: string; label: string }[]; onChange: (v: string) => void; label?: string };
  /** Akzentfarbe der Agentur (org_settings.brand_color); ohne eigene Farbe gilt die Plattformfarbe. */
  accent?: string | null;
  /** Sprache der Kopfzeile, des Sprunglinks und der Navigationsbezeichnungen (Creator-Portal zweisprachig); Standard Deutsch */
  lang?: "de" | "en";
  children?: ReactNode;
}

/** Länge des passenden Pfads eines Eintrags (-1: passt nicht). */
function matchLength(pathname: string, item: NavItem): number {
  const paths = [item.to, ...(item.also ?? [])];
  return Math.max(-1, ...paths.filter((p) => (item.end ? pathname === p : pathname === p || pathname.startsWith(p + "/"))).map((p) => p.length));
}

/**
 * Aktiver Eintrag einer Leiste: der mit dem genauesten Pfad. So gilt auf /agentur/creator/insights „Insights“ als aktiv,
 * nicht zusätzlich „Creator“ (/agentur/creator).
 */
function activeTo(pathname: string, items: NavItem[]): string | null {
  let best: NavItem | null = null, len = -1;
  for (const item of items) {
    const l = matchLength(pathname, item);
    if (l > len) { best = item; len = l; }
  }
  return best?.to ?? null;
}

/** Konto-Menü (details): schließt beim Seitenwechsel, bei Klick daneben und mit Escape. */
function useCloseOnOutside(ref: RefObject<HTMLDetailsElement | null>, pathname: string) {
  useEffect(() => {
    if (ref.current) ref.current.open = false;
  }, [ref, pathname]);
  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      const d = ref.current;
      if (d?.open && !d.contains(e.target as Node)) d.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      const d = ref.current;
      if (e.key === "Escape" && d?.open) {
        d.open = false;
        d.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref]);
}

export function PortalLayout(props: PortalLayoutProps) {
  const { pathname } = useLocation();
  const menuRef = useRef<HTMLDetailsElement>(null);
  useCloseOnOutside(menuRef, pathname);
  // Darstellung (Dunkel, Hell, Automatisch): nach der Anmeldung gilt die gewählte Darstellung aus dem Konto (0120, 0121);
  // ohne Wahl bleibt es beim Browser bzw. beim Standard Automatisch
  useThemeFromAccount();
  const sideActive = activeTo(pathname, props.nav);
  const bottomActive = activeTo(pathname, props.bottom);
  // Runder Chat-Knopf (wie im Original): Ziel und Zähler aus dem Chat-Eintrag der Navigation; auf der Chat-Seite selbst nicht
  const navLink = (item: NavItem) => {
    const active = item.to === sideActive;
    return (
      <Link key={item.to} to={item.to} className={active ? "current" : ""} aria-current={pathname === item.to ? "page" : undefined}>
        <Icon name={item.icon} />
        {item.label}
        {item.badge ? <span className="tk-badge" aria-label={item.badgeKind === "new" ? t(`${item.badge} neu`, `${item.badge} new`) : t(`${item.badge} ungelesen`, `${item.badge} unread`)}>{item.badge > 99 ? "99+" : item.badge}</span> : null}
      </Link>
    );
  };
  const accent = accentCss(props.accent);
  useDocumentTitle(portalTitle(props.orgName));
  // Vorleseprogramme sprechen die Oberfläche in ihrer Sprache (html lang)
  useDocumentLang(props.lang);
  const t = (de: string, en: string) => (props.lang === "en" ? en : de);
  if (props.vollbild) {
    return (
      <>
        <ScrollToTop />
        {accent && <style data-tk-accent="">{accent}</style>}
        <a className="skip btn" href="#main">{t("Zum Inhalt", "Skip to content")}</a>
        <header className="portal-header tk-voll-kopf">{props.vollbild}</header>
        <div className="app tk-vollbild">
          <div className="workspace"><RahmenKontext.Provider value={true}>{props.children ?? <Outlet />}</RahmenKontext.Provider></div>
        </div>
        <Toasts />
        <SteuerRueckfrageHost />
        <ERechnungHost />
      </>
    );
  }
  return (
    <>
      <ScrollToTop />
      {accent && <style data-tk-accent="">{accent}</style>}
      <a className="skip btn" href="#main">{t("Zum Inhalt", "Skip to content")}</a>
      <header className="portal-header">
        <Link className="brand co-brand" to={props.brandHref}>
          <CreatorOfficeLogo form="responsive" />
        </Link>
        <div className="header-actions">
          {props.agencyChoice && (
            <select className="agency-choice" aria-label={props.agencyChoice.label ?? t("Agentur wechseln", "Switch agency")} value={props.agencyChoice.value} onChange={(e) => props.agencyChoice!.onChange(e.target.value)}>
              {props.agencyChoice.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
          {props.bell && (
            <Link className="tk-bell" to={props.bell.href} aria-label={props.bell.unread ? t(`Benachrichtigungen, ${props.bell.unread} neu`, `Notifications, ${props.bell.unread} new`) : t("Benachrichtigungen", "Notifications")}>
              <Icon name="bell" />
              {props.bell.unread > 0 && <span className="tk-bell-count">{props.bell.unread > 99 ? "99+" : props.bell.unread}</span>}
            </Link>
          )}
          {props.gearHref && (
            <Link className="tk-bell tk-zahnrad" to={props.gearHref} aria-label={t("Alle Einstellungen", "All settings")} title={t("Alle Einstellungen", "All settings")}>
              <Icon name="settings" />
            </Link>
          )}
          <details className="account-menu" ref={menuRef}>
            <summary className="account-chip" aria-label={t(`Konto-Menü, ${props.account.name}`, `Account menu, ${props.account.name}`)}>
              <span className="account-avatar" aria-hidden="true">{props.account.initials}</span>
              <span className="account-name">{props.account.name}</span>
              <Icon name="chevron" className="icon account-caret" />
            </summary>
            <div className="account-menu-panel">
              <div className="account-menu-kopf">
                <span className="account-avatar" aria-hidden="true">{props.account.initials}</span>
                <span className="account-menu-wer">
                  <strong>{props.account.name}</strong>
                  {(props.account.org || props.account.plan) && (
                    <span className="account-menu-org">
                      {props.account.org && <span className="account-menu-orgname">{props.account.org}</span>}
                      {props.account.plan && <span className="pill account-menu-tarif">{props.account.plan}</span>}
                    </span>
                  )}
                </span>
              </div>
              <Link className="account-menu-item" to={props.account.href}><Icon name="user" />{props.account.profileLabel ?? t("Mein Profil", "My profile")}</Link>
              {props.account.securityHref && <Link className="account-menu-item" to={props.account.securityHref}><Icon name="shield" />{t("Anmeldung & Sicherheit", "Sign-in & security")}</Link>}
              {props.account.notificationsHref && <Link className="account-menu-item" to={props.account.notificationsHref}><Icon name="bell" />{t("Benachrichtigungen", "Notifications")}</Link>}
              {props.account.languageHref && <Link className="account-menu-item" to={props.account.languageHref}><Icon name="chat" />{t("Sprache", "Language")}</Link>}
              {props.account.designHref && <Link className="account-menu-item" to={props.account.designHref}><Icon name="theme" />{t("Design", "Design")}</Link>}
              {props.account.settingsHref && <><hr className="account-menu-trenner" /><Link className="account-menu-item" to={props.account.settingsHref}><Icon name="settings" />{props.gearHref ? t("Alle Einstellungen", "All settings") : t("Einstellungen", "Settings")}</Link></>}
              {props.account.aboHref && <Link className="account-menu-item" to={props.account.aboHref}><Icon name="card" />{t("Abo & Tarif", "Subscription & plan")}</Link>}
              {props.upgrade && <Link className="account-menu-item account-menu-upgrade" to={props.upgrade.to}><Icon name="upgrade" />{props.upgrade.label}</Link>}
              {props.account.helpHref && <Link className="account-menu-item" to={props.account.helpHref}><Icon name="help" />{t("Hilfe & Support", "Help & support")}</Link>}
              <button type="button" className="account-menu-item" aria-label={t("Suche öffnen", "Open search")} onClick={() => { if (menuRef.current) menuRef.current.open = false; showCommandPalette(); }}><Icon name="search" />{t("Suche öffnen", "Open search")}</button>
              <hr className="account-menu-trenner" />
              <button type="button" className="account-menu-item" aria-label={t("Hell- oder Dunkelmodus", "Light or dark mode")} onClick={toggleThemeForAccount}><Icon name="theme" />{t("Hell- oder Dunkelmodus", "Light or dark mode")}</button>
              {currentThemePreference() !== "system" && <button type="button" className="account-menu-item" onClick={autoThemeForAccount}><Icon name="theme" />{t("Wie das Gerät (automatisch)", "Like the device (automatic)")}</button>}
              {props.onSignOut && <button type="button" className="account-menu-item" onClick={props.onSignOut}><Icon name="logout" />{t("Abmelden", "Sign out")}</button>}
            </div>
          </details>
        </div>
      </header>
      <div className="app">
        <aside className="sidebar">
          <Link className="brand co-brand" to={props.brandHref}>
            <CreatorOfficeLogo form="symbol" />
          </Link>
          <small>{props.orgName.toUpperCase()}</small>
          <nav aria-label={t("Hauptnavigation", "Main navigation")}>
            {props.nav.map((item) => (item.group
              ? <Fragment key={item.to}><div className="tk-nav-gruppe" role="presentation">{item.group}</div>{navLink(item)}</Fragment>
              : navLink(item)))}
          </nav>
          {props.sideExtra}
          <small>CreatorOffice</small>
        </aside>
        <div className="workspace"><RahmenKontext.Provider value={true}>{props.children ?? <Outlet />}</RahmenKontext.Provider></div>
      </div>
      <nav className="bottomnav" aria-label={t("Mobile Navigation", "Mobile navigation")}>
        {props.bottom.map((item) => (
          <Link key={item.to} to={item.to} className={item.to === bottomActive ? "current" : ""} aria-current={pathname === item.to ? "page" : undefined}>
            <Icon name={item.icon} />
            {item.label}
          </Link>
        ))}
      </nav>
      <CommandPalette items={props.palette} lang={props.lang} search={props.suche} />
      <Toasts />
      <SteuerRueckfrageHost />
      <ERechnungHost />
    </>
  );
}

/*
 * K-463 (Formatierungstest 16.10.2026): Der runde Chat-Knopf unten rechts verdeckte Zeilenaktionen. Seit Kopf und Seitenleiste
 * stehen bleiben (K-461), ist der Chat mit Zähler in der Leiste immer sichtbar; den Knopf gibt es deshalb nicht mehr. Auf dem
 * Handy steht der Chat in der unteren Leiste.
 */
